/* THE MAPPING — A READING BECOMES A SUGGESTION SET. Phase 6.5 step 4.5a.
 *
 * ── WHAT THIS IS, AND THE ONE THING IT NEVER DOES ───────────────────
 * In: a reader's verdict (`read`, as `photo-upload.js` passes it
 * through) and the catalogue note the holding points at.
 * Out: **a suggestion set of at most two entries**, and a list of
 * REFUSALS saying why anything else was dropped.
 *
 * ⚠⚠⚠ THE MAPPING NEVER WRITES — and the ARRIVAL below it now DOES.
 * ~~It returns data; `setPending` stores it on `state`; accepting is the
 * collector's tap, in step 4.3.~~ **Corrected 2026-09-21 by his second
 * 4.3 ruling: there is NO confirm step.** `suggestionsFrom` is still
 * pure and still writes nothing; `applyReading` at the foot of this file
 * FILLS the holding — empty fields only — and marks what it filled.
 * ⚠ The split is the point: **all the judgment is in the pure half**, so
 * the half that writes has none left to get wrong.
 *
 * ── ⚠ A REFUSAL IS A STATE, NOT SILENCE ─────────────────────────────
 * Every field the verdict carries and this does not suggest comes back
 * in `refusals` with a reason. **Two states that can both occur must be
 * distinguishable when they do** — the rule that made
 * `deferred-to-worker` its own reason and `not-requested` distinct from
 * `no-engine`. A reading that produced nothing and a reading that was
 * never asked for must not look the same to 4.3.
 */

import { CATALOG, customGradeKey, gradesOf } from './catalog.js'
import { IDENTITY_FIELD, PENDING_FIELDS, RAW_FILL_FIELD, YEAR_FILL_FIELD, clearDifferent, clearLookup, clearPending, clearReading, clearUnchecked, getDifferent, getReading, isRawReading, setDifferent, setLookup, setPending, setReading, setUnchecked } from './pending.js'
/* ── STEP 6, 2026-09-26: a RAW reading narrows the catalogue instead of
 * matching an Fr. `narrow` is the pure narrower (his ruled axes, the
 * generous type map, the four states); the view calls it on every edit
 * and `pickCandidate` below checks a tapped row is still one of its
 * candidates before converting. */
import { isSized, narrowCard, yearOfText } from './note-narrow.js'
import { yearOf } from './note-filters.js'
import { cat, save } from './state.js'
import { republish } from './community.js'
/* ── STEP 7.3(c), 2026-09-23: THE ARRIVAL IDENTIFIES THE NOTE FIRST ──
 * `matchFr` is the pure matcher (7.2); `convertToRow` is the ONE
 * conversion, the same one a typed Fr runs (7.3a); `isCust` says whether
 * there is anything to identify. ⚠ `custom.js` reaches `render.js` and
 * from there this module again — an import cycle, and a benign one:
 * nothing on either side is evaluated at load, only inside functions. */
import { OTHER_TYPE, convertToRow, enterOther, isCust, isOther, saveCustom, switchToRow } from './custom.js'
import { REFUSALS, matchFr, normDenomination } from './slab-match.js'
/* ⚠⚠ `qualifierOn` ONLY, SINCE HIS RULING OF 2026-09-21. This module
 * used to import `markFor` and `withMark` because the fill WROTE THE
 * MEMO; the qualifier is now its own column and the fill sets a
 * boolean, so the three memo-writing functions are gone from here and
 * `qualifier-check`'s static line asserts they stay gone. */
import { qualifierOn } from './memo.js'

/** The grade numbers a label can print. His prompt: *"the numeric grade
 *  on the 1–70 scale, digits only"*. */
export const GRADE_MIN = 1
export const GRADE_MAX = 70

/** The two marks, and nothing else is a qualifier.
 *  ⚠ His ruling 2026-09-14: adjectival grades — `Fine`, `Choice UNC` —
 *  are NOISE, not a field. They are discarded before scoring there and
 *  dropped here, so a reader returning one suggests nothing rather than
 *  suggesting something wrong. */
export const MARKS = Object.freeze(['EPQ', 'PPQ'])

/**
 * The number a grade key carries, or null.
 *
 * ⚠ It reads the DIGITS out of the key — `Ch63` → 63, `VG10` → 10,
 * `Fr2` → 2 (Fair-2, a real grade below VG-8 on the Gold Bank notes),
 * `Gd4` → 4. `Narrow Margin` and `Wide Margin` carry no digits and are
 * therefore never matched, which is correct: his 2026-09-15 ruling says
 * those specimen rows are varieties in a price slot, not grades.
 */
export function gradeKeyNumber(key) {
  const m = /(\d+)/.exec(String(key || ''))
  return m ? Number(m[1]) : null
}

/**
 * A numeric holder grade → the key this NOTE should carry.
 *
 * His ruling 3, 2026-09-13: *"any holder grade not in the tabs defaults
 * to custom."* So: the tab on THIS note whose key embeds that number,
 * else `custom:NN`.
 *
 * ⚠⚠ AND IT IS A BIJECTION OR A REFUSAL — THERE IS NO THIRD OUTCOME.
 * `CU64`/`Ch64` and `CU66`/`Ch66` are two keys for one number, so the
 * rule could in principle face two priced tabs and have to choose.
 * **Measured 2026-09-21 over all 11,614 catalogue rows: NOT ONE row
 * carries two tabs sharing a number**, so the note's own tabs resolve it
 * every time. Where that ever stops being true this REFUSES rather than
 * picking, because choosing between two priced tabs is a valuation
 * decision with money in it — the thing the record reserved for him in
 * 2026-09-12 and which his ruling 3 deliberately took off the table.
 * ⚠ `pending-check` freezes the zero, so the day a row breaks it the
 * gate goes red rather than this function guessing.
 */
/**
 * Does this key yield a CATALOGUE price on this note?
 *
 * ⚠⚠⚠ IT IS `bookVal`'s OWN EXPRESSION AND IT MUST STAY THAT WAY.
 * `state.js` prices a holding as `gradesOf(n).indexOf(h.grade)` → `n.g[i]`,
 * so anything else here would be a second opinion about money.
 *
 * ⚠⚠ THE FIRST WRITING SAID `priced: true` WHENEVER THE KEY WAS A TAB,
 * AND THAT WAS WRONG ON 3,494 REAL CATALOGUE ROWS — every fully-unpriced
 * row carries the seven tabs with all seven prices `null`. It is wrong
 * on every CUSTOM-ENTRY note too, which `quickAdd` builds with the
 * standard seven tabs and seven nulls. **His case found it before the
 * case was written.** ⚠ It matters because `priced` is what 4.3 reads to
 * decide whether to say *"No catalog price for this grade — add your own
 * value"*: a wrong `true` means the app stays SILENT on a note that just
 * left the vault total, which is the exact failure his ruling exists to
 * prevent.
 */
export function pricedAt(note, key) {
  const gs = gradesOf(note) || []
  const i = gs.indexOf(key)
  return i >= 0 && note && Array.isArray(note.g) && note.g[i] != null
}

export function gradeKeyFor(note, n) {
  if (!Number.isInteger(n) || n < GRADE_MIN || n > GRADE_MAX) {
    return { ok: false, reason: 'grade-out-of-range' }
  }
  const tabs = gradesOf(note) || []
  const hits = tabs.filter((k) => gradeKeyNumber(k) === n)
  if (hits.length > 1) return { ok: false, reason: 'grade-ambiguous', keys: hits }
  if (hits.length === 1) return { ok: true, key: hits[0], priced: pricedAt(note, hits[0]) }
  /* ⚠ NOT A FAILURE — his ruling. 80% of the grades a label can print
   * have no tab on an average note, and `custom:NN` is the home the
   * Custom chip exists to give them. ⚠⚠ It is UNPRICED by
   * construction (`bookVal` indexes the note's own price columns and
   * this key is never in them), so accepting it can take a note out of
   * the vault total and into 3.4's unpriced count. Recorded at 4.3. */
  return { ok: true, key: customGradeKey(n), priced: false }
}

/** The qualifier a reading carries → a BOOLEAN proposal, or nothing.
 *
 * ⚠⚠⚠ HIS RULING, 2026-09-21, AND IT IS THE WHOLE SHAPE OF THIS FIELD:
 * **the suggestion proposes turning the toggle ON and nothing more.**
 * So the value is `true`, never a word.
 *
 * ⚠⚠ THAT RULING OUTLIVED ITS OWN REASON, AND THE VALUE DID NOT CHANGE.
 * ~~When he took it, the reason was that `markFor` read the service off
 * the memo and decided between EPQ and PPQ, so a value carrying a word
 * would have been this module taking a decision the toggle owned.~~
 * **His LATER ruling the same day made the qualifier its own column and
 * took the letters out of the memo entirely, so there is no word left
 * for anyone to decide.** A boolean is now simply what the field IS —
 * and `on` means "EPQ/PPQ", the two being indistinguishable ON PURPOSE
 * (his ruling 3). Recorded rather than quietly re-derived, because the
 * proposal's shape stayed identical through a reversal of its premise.
 *
 * ⚠ `MARKS` still gates what COUNTS as a qualifier on the label — the
 * reader must have seen EPQ or PPQ and not some other word — which is a
 * reading of the printed slab, not a write to the memo.
 */
export function qualifierProposal(raw) {
  const v = String(raw == null ? '' : raw).trim().toUpperCase()
  if (!v) return { ok: false, reason: 'qualifier-absent' }
  if (!MARKS.includes(v)) return { ok: false, reason: 'qualifier-not-a-mark' }
  return { ok: true, value: true }
}

/**
 * The verdict → the suggestion set.
 *
 * Returns `{ suggestions, refusals, reason }`. `suggestions` is an array
 * of `{ field, value, source, engine, ms }` in `PENDING_FIELDS` order,
 * ready for `setPending`. **Nothing is written.**
 */
export function suggestionsFrom(verdict, note) {
  const out = { suggestions: [], refusals: [], reason: null }
  const v = verdict || null

  /* ⚠ FOUR REFUSALS, EACH ITS OWN REASON. A verdict that failed, a
   * verdict with nothing in it, and a verdict whose every field was
   * dropped are three different facts, and 4.3 shows different things
   * for them. */
  if (!v || typeof v !== 'object') { out.reason = 'no-verdict'; return out }
  if (v.ok !== true) { out.reason = v.reason ? 'verdict-' + v.reason : 'verdict-not-ok'; return out }
  const fields = v.fields && typeof v.fields === 'object' ? v.fields : null
  if (!fields || Object.keys(fields).length === 0) { out.reason = 'no-fields'; return out }

  const meta = {
    source: 'read',
    engine: v.engine || null,
    ms: v.ms ?? null,
  }

  for (const field of PENDING_FIELDS) {
    if (field === 'grade') {
      const raw = fields.grade
      if (raw == null || String(raw).trim() === '') { out.refusals.push({ field, reason: 'absent' }); continue }
      /* ⚠ The prompt asks for digits only. A value that is not a clean
       * integer is refused rather than coerced — `parseInt('64 Choice')`
       * would happily return 64 and that is a guess. */
      const s = String(raw).trim()
      if (!/^\d{1,2}$/.test(s)) { out.refusals.push({ field, reason: 'not-digits', got: s }); continue }
      const r = gradeKeyFor(note, Number(s))
      if (!r.ok) { out.refusals.push({ field, reason: r.reason, keys: r.keys }); continue }
      out.suggestions.push({ field, value: r.key, priced: r.priced, ...meta })
      continue
    }
    if (field === 'qualifier') {
      const r = qualifierProposal(fields.qualifier)
      if (!r.ok) { out.refusals.push({ field, reason: r.reason }); continue }
      out.suggestions.push({ field, value: r.value, ...meta })
      continue
    }
    /* ⚠ Unreachable while `PENDING_FIELDS` is the two above. It is here
     * so that ADDING a field without teaching this function about it
     * refuses loudly instead of silently suggesting nothing. */
    out.refusals.push({ field, reason: 'no-rule-for-field' })
  }

  if (out.suggestions.length === 0 && out.reason == null) out.reason = 'nothing-suggestable'
  return out
}

/* ══ 4.5b / 4.3 — THE ARRIVAL, AND THE FILL ════════════════════════════
 *
 * ⚠⚠ THE HALF ABOVE THIS LINE IS PURE AND THE HALF BELOW IS NOT, AND
 * THAT IS THE WHOLE REASON THEY ARE SEPARATE. `suggestionsFrom` can be
 * reasoned about and twinned against fixtures; `applyReading` touches
 * `state` and now WRITES. Keeping the decision in the pure half means
 * the impure half has no judgment left to get wrong.
 */

/**
 * THE APP'S AUTOMATIC STARTING GRADE FOR A NOTE.
 *
 * ⚠⚠⚠ THIS IS `startAdd`'s OWN EXPRESSION AND IT MUST STAY THAT WAY —
 * *the first PRICED grade, else the first grade, else VF20*
 * (`custom.js:50`). If the two ever diverge, this function starts
 * answering a question about a grade the app no longer assigns, and the
 * AI would overwrite a collector's choice believing it was a default.
 *
 * ⚠⚠ THE TWO ADD PATHS DIVERGE AND THIS BRANCHES FOR IT. `quickAdd`
 * hardcodes `"VF20"` for a custom entry, while the formula on that same
 * note (seven tabs, seven null prices) would give `"VG8"`. **Measured,
 * not assumed** — and `n.cust` is what tells them apart.
 *
 * Measured 2026-09-21 over 11,614 rows: `VG8` 45.2% · `EF40` 20.9% ·
 * `VF20` 19.8% · `Ch63` 9.8%, nine others 4.3%.
 */
export function autoGradeFor(note, holding) {
  if (!note) return 'VF20'
  /* ⚠ OTHER'S STEP 5, HIS RULINGS: an Other note starts at NO grade (ruling 6),
   * so a VF20 on it is his, never automatic; and a BLANK grade counts as
   * automatic on every custom note (his B leaves one on a note that left
   * Other) — so a slab label's grade still fills it with ✨, and a conversion
   * gives the row its automatic grade. */
  if (note.cust && (isOther(note) || (holding && !String(holding.grade || '').trim()))) return ''
  if (note.cust) return 'VF20'
  const gs = gradesOf(note) || []
  const g = note.g || []
  return gs.find((k) => g[gs.indexOf(k)] != null) || gs[0] || 'VF20'
}

/**
 * Has the collector picked this grade themselves?
 *
 * ⚠⚠⚠ THE DERIVED TEST, HIS RULING 2026-09-21, AND ITS COST IS RECORDED
 * IN ITS STRONGER FORM RATHER THAN BORROWED FROM 4.6.
 * *"For GRADE, treat 'empty' as 'still the app's automatic starting
 * grade — the collector hasn't picked one.'"* So the AI may replace the
 * automatic grade and never a picked one, and **nothing is stored**: a
 * flag would be a new field, a new column and a migration, which his
 * decision 3 forbids.
 *
 * ⚠ **IT IS WRONG IN EXACTLY ONE CASE: the collector deliberately taps
 * the grade the app would have chosen anyway.** Then the AI overwrites a
 * choice they made. ⚠⚠ At 4.6 his accepted cost was *a suggestion style
 * on a correct value* — COSMETIC. **This one changes a VALUE, on the
 * field where a wrong value misprices the note**, and the automatic
 * grade is the LOWEST priced one — `VG8` on 45% of rows — so the
 * collector it bites is the one cataloguing a genuinely worn note.
 * ⚠⚠⚠ **HIS ANSWER, AND IT IS WHY THIS IS SAFE RATHER THAN MERELY
 * CHEAP: the ✨ shows the change and they can tap theirs back.** The
 * marker is the whole of the recovery — a star that failed to appear
 * would make this cost silent, which is why the gate asserts it.
 */
export const gradeIsCollectors = (holding, note) =>
  !!holding && holding.grade !== autoGradeFor(note, holding)

/**
 * ══ STEP 7.3(c), 2026-09-23 — THE SLAB LOOKUP'S ARRIVAL ═══════════════
 *
 * A reading's three identity fields (`fr_number`, `star_label`,
 * `star_serial`) become ONE catalogue row or a NAMED refusal (7.2), and
 * this is where that answer reaches the note. His rulings, 2026-09-22/23:
 *
 *   WHO   the ADD-BUTTON path only — a custom note whose `n.fr` is BLANK
 *         at the moment the landing arrives. A catalogue row is settled
 *         (the collector picked it, or an earlier conversion did). A
 *         typed Fr is THEIRS and is never overwritten — not even a line,
 *         because the line says what to do and they have done it. ⚠ His
 *         confirmation: the guard is the Fr field ALONE; a typed cost,
 *         year or memo does not stop the lookup.
 *   MATCH the SAME conversion a typed Fr runs (`convertToRow`), the
 *         automatic grade carried across onto the NEW row (finding 1 —
 *         otherwise `VF20` from `quickAdd` reads as collector-picked on
 *         every row whose first priced tab is not VF20, Star 3's among
 *         them), and a marker on `catId` so the hero shows the ✨ and the
 *         "Not the right note?" button. Any earlier refusal line goes.
 *   REFUSE one plain line, never silence — WHICH refusal, because each
 *         names a different thing to do. ⚠ `no-fr` speaks ONLY when a
 *         grading service was read: a raw note added with the button has
 *         no Fr to read and stays silent (his ruling 3).
 *   FAIL  `read.ok !== true` — nothing was tried, and nothing is said or
 *         cleared. A failure and a refusal must look different on the
 *         screen, and a BACK landing (`not-requested`) must not clear
 *         the line the front put there.
 *
 * Returns what happened, for the caller to speak about or not:
 * `{ converted: row }` · `{ refused: reason }` · `{ silent: why }` · null
 * when there was nothing to identify. ⚠ It never toasts: the boot sweep
 * lands with the collector anywhere in the app.
 */
export function identifyNote(state, h, read) {
  if (!read || read.ok !== true) return null
  const note = cat(h.catId)
  if (!isCust(note)) return differentNote(state, h, read)
  if (note.fr) return { silent: 'typed-fr' }
  /* ══ STEP 6 — A RAW READING, HIS RULING 2026-09-26 ═══════════════════
   * No label, no Fr: the six fields go on the card (`state.reading`,
   * memory-only) and the collector finishes. ⚠ Checked HERE, after the
   * two guards above and before `matchFr`: ~~a catalogue note's raw photo
   * says nothing (7.5's prompt needs a confident Fr match, which a raw
   * reading cannot give)~~ — since his rulings of 2026-10-06 a catalogue
   * note's raw photo is `differentNote`'s, which compares its DENOMINATION
   * (above, the first guard); a typed Fr stays silent. */
  if (isRawReading(read)) return rawReading(state, h, note, read)
  /* ══ HIS ANSWER 2 ON OTHER — A HAND PICK OF OTHER STICKS ═══════════════
   * *"Only a pick of Other I make by hand after a photo converted the note
   * sticks. An Other the AI filled doesn't stick."* — and 4b: *"Once the
   * collector sets a note back to Other, a later photo doesn't convert it
   * again. Their choice sticks."* `keepOther` is set by Note details' Type
   * (main.js) only on an entry "Not the right note?" made (`reverted`,
   * custom.js), so a label's Fr no longer converts it; a raw photo still
   * draws the card, above. On a replaced front the unchecked line says so
   * (his answer 5, `applyReading`). Private: `custom_entry` reaches a
   * stranger only through `db/009`'s four keys. */
  if (note.keepOther) return { silent: 'kept-other' }
  const fields = read.fields && typeof read.fields === 'object' ? read.fields : {}
  const m = matchFr(fields, CATALOG)
  if (m.ok) {
    /* ⚠ The automatic-grade test is asked against the CUSTOM note BEFORE
     * the conversion and the answer carried across: a grade that was
     * still automatic is still automatic on the new row, and becomes
     * that row's automatic grade so the value fill below can see it as
     * empty. A grade the collector tapped stays exactly as it is
     * (`convertToRow` re-picks it only if the row lacks the tab). */
    const wasAutomatic = !gradeIsCollectors(h, note)
    const before = { year: h.year, grade: h.grade }
    /* ⚠⚠ THE GRADE IS SET BEFORE THE CONVERSION, NOT AFTER — HIS CATCH,
     * 2026-09-23: `convertToRow` SAVES, so a grade set after it would sit
     * unsaved until the next autosave whenever the label carries no grade
     * to fill. Set first, it is in the bytes the conversion writes;
     * `convertToRow` keeps a grade the row has, and the automatic grade
     * is one of the row's own tabs by construction. */
    if (wasAutomatic) h.grade = autoGradeFor(m.row)
    convertToRow(h, m.row)
    /* ⚠ WHAT THE CONVERSION ITSELF FILLED goes on the marker — his detail,
     * 2026-09-23: on "Not the right note?" the year from the wrong row's
     * series and a grade the conversion moved are cleared, because the
     * collector did not type them and they belong to the wrong note. A
     * value that did not move is null here and is never touched. */
    const filled = {
      year: h.year !== before.year ? h.year : null,
      grade: h.grade !== before.grade ? h.grade : null,
    }
    setPending(state, h.id, IDENTITY_FIELD, { value: m.row.sid, source: 'read', engine: read.engine || null, ms: read.ms ?? null, filled })
    clearLookup(state, h.id)
    return { converted: m.row, fr: m.fr }
  }
  if (m.reason === REFUSALS.NO_FR && !String(fields.service == null ? '' : fields.service).trim()) {
    return { silent: 'no-fr-on-a-raw-note' }
  }
  setLookup(state, h.id, { reason: m.reason, fr: m.fr, read: m.read, want: m.want })
  return { refused: m.reason, fr: m.fr }
}

/**
 * ══ STEP 6 — THE RAW READING ARRIVES: THE CARD, AND ~~ONE FILL~~ TWO ═══
 *
 * The reading goes on `state.reading`; any earlier lookup line was about
 * a photograph this one replaces and goes. Then his ruling (2): *"fill
 * the note's EMPTY Denomination from the reading with ✨ when no row is
 * tapped, never Type."* ~~Nothing else on the note moves~~ — ⚠ "never
 * Type" is SUPERSEDED by his ruling of 2026-10-05 (`syncNoFrFill`, below):
 * Note details' Type follows the card's. The app still never picks, and
 * the card's other values live only on `state`.
 */
function rawReading(state, h, note, read) {
  const r = setReading(state, h.id, { fields: read.fields, engine: read.engine || null, ms: read.ms ?? null })
  clearLookup(state, h.id)
  const filled = fillFromReading(state, h, note)
  return { raw: true, reading: r, filled: filled ? ['denom'] : [] }
}

/**
 * The two fills a reading makes on a custom entry — its Type
 * (`syncNoFrFill`) and its EMPTY Denomination (`fillDenomination`) — SAVED
 * ONCE (his catch of 2026-09-27: a save is a server write). Before his
 * 2026-10-05 ruling the Type fill fired only on a no-Fr answer; now it
 * fires on most readings, and two saves would mean two server writes per
 * front photo. Called on the arrival and after "Not the right note?".
 * Returns true when the Denomination was filled.
 */
export function fillFromReading(state, h, note) {
  syncNoFrFill(state, h, { save: false })
  const filled = fillDenomination(state, h, note)
  if (filled) { const r = getReading(state, h.id); if (r) r.noFrUnsaved = false }
  /* ⚠ OTHER'S STEP 5: the denomination's save is the entry's alone; a VF20 `enterOther` took off is the holding's */
  if (filled) { const r = getReading(state, h.id); if (r && r.noFrGrade) { r.noFrGrade = false; save() } }
  else flushNoFrFill(state, h)
  return filled
}

/**
 * His ruling (2). The custom entry's Denomination fills from the card's
 * denomination ONLY when it is empty (rule 2 of 4.3: never a typed
 * value), with the ✨ marker on `RAW_FILL_FIELD`. ~~Type is never touched.~~
 * (Type is `syncNoFrFill`'s since his ruling of 2026-10-05.)
 * Called on the arrival, and again after "Not the right note?" makes a
 * new blank entry while the card is still on the screen — both through
 * `fillFromReading`, which saves once.
 * Returns true when it wrote.
 */
export function fillDenomination(state, h, note) {
  const r = getReading(state, h.id)
  if (!r || !note || !isCust(note)) return false
  const d = r.fields.denomination
  if (!d || String(note.denom || '').trim()) return false
  note.denom = d.slice(0, 14); note.fd = note.denom
  saveCustom(); republish()
  setPending(state, h.id, RAW_FILL_FIELD, { value: note.denom, source: 'read', engine: r.engine, ms: r.ms })
  return true
}

/**
 * ══ STOP B — THE NO-FR FILL, HIS RULING 3 OF 2026-09-27 ═══════════════
 *
 * *"When the lookup ends in "no Fr" and every match is one type, the note
 * is saved with that type (this overrides my ruling (2) "never Type" for
 * that case only) and keeps its state."* So while the card's answer is
 * no-Fr over ONE section, the custom entry's EMPTY Type (`Custom entry`)
 * takes that section and its EMPTY desc takes the card's State or colony
 * — the catalogue's own Obsolete shape, where `obsState` reads the first
 * part — each marked on the card's entry (`r.noFr`) so Note details shows
 * the ✨. The fills FOLLOW the card while it is up (an edit that stays in
 * no-Fr moves them), and when an edit LEAVES no-Fr each is cleared back
 * to empty — only while still unmarked by the collector: a Type or a
 * state the collector set in Note details drops its mark and is never
 * touched here again. Called on the reading's arrival and after every
 * card edit. Returns true when it wrote the note.
 * ⚠⚠ SAVED ONCE PER EDIT, NEVER PER KEYSTROKE — HIS CATCH 2026-09-27.
 * `saveCustom()` is a SERVER WRITE (`remote.js` `syncVault` → `POST
 * rpc/sync_holdings`, the whole vault, no debounce), and `republish()`
 * re-reads the community list for a public collector. A card's text field
 * calls this on every keystroke with `{ save: false }`: the note changes
 * in memory (the hero and Note details show it live) and `r.noFrUnsaved`
 * says so; the field's `change` — when the edit settles, the moment every
 * Note details field saves — calls `flushNoFrFill`, and so do Save and
 * leaving, before the card is forgotten. A select, the size answer and
 * the reading's arrival are one event each and save at once (the arrival
 * through `fillFromReading`, once with the Denomination).
 *
 * ══ HIS RULING OF 2026-10-05 — NOTE DETAILS' TYPE FOLLOWS THE CARD'S ══
 * *"on a custom note, Note details' Type follows the reading card's Type —
 * filled only while Note details still reads "Custom entry", marked ✨
 * like the Denomination, never overwriting a type the collector chose."*
 * — and, read back to him and confirmed: *"once filled, Type moves with
 * the card's Type and returns to "Custom entry" when the card names none,
 * until the collector picks a Type themselves."* So the Type this fills is
 * the no-Fr section when there is one (his ruling 3), else the card's own
 * Type whenever it names ONE option (`isSized`: picked, read as one, or
 * derived — never a question, a mixed or an unknown heading). The two
 * cannot disagree: only Obsolete rows lack an Fr (850 of 850, measured
 * 2026-10-05), and the card's "Obsolete Note" option is that section alone.
 * `r.noFr.section` is the ✨ for both, despite its name.
 * ⚠ THE COLLECTOR'S OWN CHOICE IS FINAL — his yes, the same day: a Type or
 * a State or colony the collector sets in Note details (main.js), "Custom
 * entry" and an empty state included, is recorded against THAT entry
 * (`sectionMine` / `descMine` = its sid) and never touched here again while
 * the card is up. Measured before: a deliberate "Custom entry" came back as
 * the fill's on the next card edit. (A cleared state did not — his #11
 * carries the empty value back to the card — but a state typed on the card
 * afterwards flowed into the field the collector had set; his ruling closes
 * that too.) "Not the right note?" makes a NEW entry, which may be filled.
 *
 * ══ HIS RULINGS ON OTHER'S STEP 3, 2026-10-26 — THE AI CALLS IT OTHER ══
 * *"Other fills when the heading is one the app doesn't know, or when
 * nothing matches."* — and *"a Type that's simply empty doesn't fill Other
 * while the card still shows catalogue notes to tap"* — and *"A misread
 * detail filling Other is fine: the ✨ shows the AI did it, and fixing the
 * detail puts the Type back."* So Other is the fill when the card's Type is
 * a heading the map does not know (`unknown`) or its list is `nothing`, and
 * it outranks a read Type; it follows the card like any fill, and an Other
 * filled here is the fill's, never the collector's (his answer 2: it does
 * not stick). ⚠ HIS OPTION 1, THE SAME NIGHT: *"When an unknown heading's
 * details reach a single no-Fr row, the no-Fr type wins: it fills Obsolete
 * Note with its state, as before step 3, so the card's line stays true."*
 */
export function syncNoFrFill(state, h, { save: persist = true } = {}) {
  const r = h ? getReading(state, h.id) : null
  const note = h ? cat(h.catId) : null
  if (!r || !note || !isCust(note) || note.fr) return false
  if (!r.noFr) r.noFr = {}
  const { card, res } = narrowCard(r, CATALOG)
  const noFrRow = res.state === 'no-fr' && res.count === 1
  const other = res.state === 'nothing' || (card.type.kind === 'unknown' && !noFrRow)
  const types = res.state === 'no-fr' ? [...new Set(res.candidates.map((c) => c.section))] : []
  const one = types.length === 1 ? types[0] : null
  const st = one ? String(card.input.state || '').trim().slice(0, 40) : ''
  /* ⚠ HIS RULING ON OTHER'S STEP 4: *"After 'Not the right note?', the AI
   * doesn't fill Other on that note; the Type stays Custom entry and the Fr
   * field stays visible, so I can pick Other by hand or find the right
   * catalogue note."* `reverted` is that entry's stamp (custom.js). */
  const sec = other ? (note.reverted ? null : OTHER_TYPE) : one || (isSized(card.type) ? card.type.section : null)
  const typeMine = r.noFr.sectionMine === note.sid, stateMine = r.noFr.descMine === note.sid
  let wrote = false
  if (sec && !typeMine && (r.noFr.section || note.section === 'Custom entry')) {
    if (note.section !== sec) {
      /* ⚠ OTHER'S STEP 5, HIS A: the AI putting the note INTO Other takes a VF20
       * off, as his pick does (custom.js `enterOther`, which keeps a label's) */
      if (sec === OTHER_TYPE && enterOther(h)) r.noFrGrade = true
      note.section = sec; wrote = true
    }
    r.noFr.section = true
  } else if (!sec && r.noFr.section) { note.section = 'Custom entry'; delete r.noFr.section; wrote = true }
  if (st && !stateMine && (r.noFr.desc || !String(note.desc || '').trim())) {
    if (note.desc !== st) { note.desc = st; wrote = true }
    r.noFr.desc = true
  } else if (!st && r.noFr.desc) { note.desc = ''; delete r.noFr.desc; wrote = true }
  if (wrote && !persist) r.noFrUnsaved = true
  else if ((wrote || r.noFrUnsaved) && persist) { r.noFrUnsaved = false; saveCustom(); republish() }
  /* the grade `enterOther` took off is the holding's, saved with the Type */
  if (persist && r.noFrGrade) { r.noFrGrade = false; save() }
  return wrote
}

/** Save a fill the keystrokes left in memory — once. The card's text
 *  field's `change`, Save, and leaving call it. Returns true when it saved. */
export function flushNoFrFill(state, h) {
  const r = h ? getReading(state, h.id) : null
  if (!r || !r.noFrUnsaved) return false
  r.noFrUnsaved = false
  if (r.noFrGrade) { r.noFrGrade = false; save() }
  saveCustom(); republish()
  return true
}

/**
 * The collector taps a candidate row — the ONE way a raw reading reaches
 * the note's identity. The SAME conversion a typed Fr runs
 * (`convertToRow`), the automatic grade carried across as `identifyNote`
 * does, and the identity marker with `source: 'narrowed'` so the hero's
 * ✨ and "Not the right note?" show. ⚠ The row must be a CURRENT
 * candidate of the card as it stands: a stale sid converts nothing. The
 * card's reading stays on `state`, so the revert can bring it back.
 * Returns the row, or null when nothing was converted.
 */
export function pickCandidate(state, h, sid) {
  const note = cat(h.catId)
  /* ⚠ 2026-10-06: a CATALOGUE note's card is the one "See likely notes"
   * opened — its tap switches the note (his ruling 4), never converts it */
  if (note && !isCust(note)) return pickForCatalogue(state, h, sid)
  if (!note || !isCust(note) || note.fr) return null
  const r = getReading(state, h.id)
  if (!r) return null
  const row = cat(sid)
  if (!row || isCust(row)) return null
  /* ⚠ A ROW CAN BE CHOSEN ONLY FROM A LIST THE CARD OFFERED — the
   * `narrowed` state. His rulings: *"a no-Fr answer ends the lookup"* and
   * 6.5's *"never offer them"* over twelve. Found by the twin (U9,
   * 2026-09-26): the no-Fr set for his Michigan front is ONE row, the
   * Obsolete row with no Fr, and without this line a call here would
   * convert the note to it even though no button offers it. */
  /* ⚠ STOP B: THE CARD'S OWN LIST — `narrowCard`, the call the view draws
   * the rows with (the Type option, the set-aside, the Charter period from
   * the year), never the raw fields: a row the card did not offer must
   * not be choosable, and a row it offered must be. */
  const { res } = narrowCard(r, CATALOG)
  if (res.state !== 'narrowed') return null
  if (!res.candidates.includes(row)) return null
  const wasAutomatic = !gradeIsCollectors(h, note)
  const before = { year: h.year, grade: h.grade }
  if (wasAutomatic) h.grade = autoGradeFor(row)
  convertToRow(h, row)
  const filled = { year: h.year !== before.year ? h.year : null, grade: h.grade !== before.grade ? h.grade : null }
  setPending(state, h.id, IDENTITY_FIELD, { value: row.sid, source: 'narrowed', engine: r.engine, ms: r.ms, filled })
  clearPending(state, h.id, RAW_FILL_FIELD)
  clearLookup(state, h.id)
  return row
}

/**
 * ══ STEP 7.5, HIS RULING 2026-09-25 — A CATALOGUE NOTE WHOSE PHOTO IS A
 * DIFFERENT NOTE ═══════════════════════════════════════════════════════
 *
 * His walk: he replaced a note's photo with another note's, and nothing
 * happened — the reading was made (a front photo always is) and this
 * function returned `null` for any catalogue row. Now it compares:
 *
 *   SAME ROW   the photo is this note. `null`, exactly as before, and any
 *              prompt an earlier photo left is cleared — it was about a
 *              photograph that is no longer on the note.
 *   DIFFERENT  a CONFIDENT match (`m.ok`) to another row → the prompt, and
 *              NOTHING ELSE: no conversion, no fill. The collector decides.
 *   REFUSED    any refusal, ambiguous among them → silent, and an earlier
 *              prompt is cleared for the same reason. A weak reading must
 *              not ask; his rule is a prompt only on a confident match.
 *
 * ⚠ ONLY A CATALOGUE NOTE. A custom note with a typed Fr stays silent —
 * his 7.3 ruling, "not even a line". A blank custom note is 7.3's lookup.
 *
 * ⚠ HIS RULINGS OF 2026-10-06 — A RAW READING (no Fr to match) compares
 * the DENOMINATION alone (`rawDifferent`, below). And a new photo SUPERSEDES
 * a card that "See likely notes" opened from the last one: the card goes,
 * whatever this reading says.
 */
function differentNote(state, h, read) {
  clearReading(state, h.id)
  const fields = read.fields && typeof read.fields === 'object' ? read.fields : {}
  if (isRawReading(read)) return rawDifferent(state, h, read, fields)
  const m = matchFr(fields, CATALOG)
  if (m.ok && m.row.sid !== h.catId) {
    setDifferent(state, h.id, { sid: m.row.sid, read, mark: state.screen !== 'holding' })
    return { different: m.row }
  }
  clearDifferent(state, h.id)
  return null
}

/**
 * ══ HIS RULINGS OF 2026-10-06 — A CATALOGUE NOTE'S RAW FRONT PHOTO ══════
 *
 * *"Build the prompt for a catalogue note's raw front photo, denomination
 * only (not type)"*. The read denomination and the note's, each through
 * `normDenomination` (the slab path's own rule: `$5`, `Five Dollars` and
 * `5 Dollars` are one value; `50¢` and `Fifty Cents` too). They DIFFER →
 * the prompt, and NOTHING ELSE: no conversion, no fill — the identity's
 * `different` makes `applyReading` refuse every fill, as on 7.5's path.
 * Anything else is SILENT and clears an earlier prompt, as 7.5's refusals
 * do: the same denomination (a type that differs included — his ruling:
 * the type was wrong on 3 of his own 19), no denomination read, or a value
 * either side cannot be compared (43 rows, measured: Error `—`, Colonial
 * "(written)", "Between $50–$100").
 */
/* ONE rule for "can these two denominations be compared" — `rawDifferent`'s,
 * hoisted 2026-10-22 so `sameDenominationRaw` (his ruling of that day) asks
 * the same question rather than a second one that agrees today. */
const comparableDenom = (k) => !!k && !k.startsWith('text:')
function rawDifferent(state, h, read, fields) {
  const note = cat(h.catId)
  const said = String(fields.denomination == null ? '' : fields.denomination).trim()
  const a = normDenomination(said), b = normDenomination(note && note.denom)
  const comparable = comparableDenom
  if (comparable(a) && comparable(b) && a !== b) {
    setDifferent(state, h.id, { raw: true, read, mark: state.screen !== 'holding' })
    return { different: { raw: true, read: said, want: note.denom } }
  }
  clearDifferent(state, h.id)
  return null
}

/** His ruling of 2026-10-22: did a RAW reading on a CATALOGUE note say the
 *  SAME denomination? Such a reading can neither confirm nor contradict the
 *  note (the raw prompt compares denomination alone), so on a replaced
 *  front `applyReading` draws the unchecked line. The identity stays null,
 *  as the gate has pinned it since 2026-10-06; this is asked beside it. */
function sameDenominationRaw(h, read) {
  const note = cat(h.catId)
  if (!note || isCust(note) || !isRawReading(read)) return false
  const fields = read.fields && typeof read.fields === 'object' ? read.fields : {}
  const a = normDenomination(String(fields.denomination == null ? '' : fields.denomination).trim())
  const b = normDenomination(note.denom)
  return comparableDenom(a) && comparableDenom(b) && a === b
}

/** His ruling 2(b) of 2026-10-25, the incomparable denomination: a RAW reading on a CATALOGUE note whose
 *  denomination cannot be compared with the note's — none read (*"Include 'no denomination read'"*), or a
 *  value either side `comparableDenom` refuses (Error `—`, Colonial "(written)", "Between $50–$100").
 *  `rawDifferent` stays silent on exactly these; on a replaced front they draw the unchecked line. */
function incomparableDenominationRaw(h, read) {
  const note = cat(h.catId)
  if (!note || isCust(note) || !isRawReading(read)) return false
  const fields = read.fields && typeof read.fields === 'object' ? read.fields : {}
  const a = normDenomination(String(fields.denomination == null ? '' : fields.denomination).trim())
  const b = normDenomination(note.denom)
  return !(comparableDenom(a) && comparableDenom(b))
}

/** His ruling 2(a) of 2026-10-25, the inconclusive label: *"Every Fr-matching refusal on a catalogue note's
 *  label reading counts: no Fr read, ambiguous, or not in the catalogue. A label that matches this same note
 *  stays silent."* And his ruling on the other three, the same night: *"All six matchFr refusals count, including
 *  star-unsure, denomination-mismatch and series-mismatch. In each one the app can't confirm the note, so the
 *  unchecked line shows."* A LABEL reading on a CATALOGUE note whose `matchFr` refuses — any of the six `REFUSALS`.
 *  `differentNote` stays silent on these; a confident match to this row stays silent as a confirmation, and a
 *  confident match to another row is 7.5's prompt, not this. */
function inconclusiveLabel(h, read) {
  const note = cat(h.catId)
  if (!note || isCust(note) || isRawReading(read)) return false
  const fields = read.fields && typeof read.fields === 'object' ? read.fields : {}
  return !matchFr(fields, CATALOG).ok
}

/**
 * "See likely notes" — his ruling 3 of 2026-10-06: *"opens the whole card,
 * fields and list, as on a custom note."* The prompt's reading goes on
 * `state.reading` (memory-only) and the prompt is marked open; the view
 * draws the card under it. Its edits re-narrow as on a custom note, and
 * every write the card makes on a custom entry is guarded away from a
 * catalogue row (`fillDenomination`, `syncNoFrFill` and `pickCandidate`
 * each test `isCust`). Returns true when it opened.
 */
export function seeLikelyNotes(state, h) {
  const d = h ? getDifferent(state, h.id) : null
  const note = h ? cat(h.catId) : null
  if (!d || !d.raw || !note || isCust(note)) return false
  setReading(state, h.id, { fields: d.read.fields, engine: d.read.engine, ms: d.read.ms })
  d.open = true
  return true
}

/**
 * A row tapped on the card that "See likely notes" opened — his ruling 4 of
 * 2026-10-06: *"A row tap on a catalogue note leaves EPQ/PPQ as it is (a
 * raw reading says nothing about it); otherwise it switches the way Update
 * this note does: year follows, grade back to automatic, cost and memo
 * kept."* `switchToRow`, never `convertToRow` (which would drop a catalogue
 * row from `BY_SID`); the qualifier passed through unchanged; the grade the
 * new row's automatic one, any grade ✨ from an earlier photo gone; the
 * hero's ✨ as after Update (`source: 'different'`, so no "Not the right
 * note?"). The row must be one the card OFFERS, as on a custom note.
 * ⚠ THE BUILDER'S, beyond his rulings: a tap on the row this note ALREADY
 * is (the collector corrected the card's denomination back to the note's)
 * changes nothing and closes the prompt and the card — "this is my note".
 */
function pickForCatalogue(state, h, sid) {
  const d = getDifferent(state, h.id), r = getReading(state, h.id)
  if (!d || !d.raw || !d.open || !r) return null
  const row = cat(sid)
  if (!row || isCust(row)) return null
  const { res } = narrowCard(r, CATALOG)
  if (res.state !== 'narrowed' || !res.candidates.includes(row)) return null
  clearDifferent(state, h.id)
  clearReading(state, h.id)
  if (row.sid === h.catId) return null
  const y = yearOf('', row)
  switchToRow(h, row, { grade: autoGradeFor(row), year: y ? String(y) : '', qualifier: h.qualifier })
  clearPending(state, h.id, 'grade')
  setPending(state, h.id, IDENTITY_FIELD, { value: row.sid, source: 'different', engine: r.engine, ms: r.ms })
  return row
}

/**
 * "Update this note" — the collector's tap on the 7.5 prompt.
 *
 * His rulings, 2026-09-25: the note becomes the photo's row; the GRADE is
 * the label's with a ✨ (or the new row's starting grade when none was
 * read); the YEAR follows the new row's series *"even if one is already
 * filled — the tap is my consent, same as the grade"*, and CLEARS when the
 * new row has no series year (his review of the renders, 2026-09-25); the EPQ/PPQ
 * qualifier follows the grade (the reading's, with its ✨, or OFF — the
 * write is in `switchToRow`); cost and memo stay.
 * ⚠ Not `convertToRow`: that is custom → row and drops the old entry from
 * `BY_SID`, which on a CATALOGUE sid would delete the catalogue's own row.
 * Returns the row, or null when there was no prompt to answer.
 */
export function updateToDifferent(state, h) {
  const d = getDifferent(state, h.id)
  if (!d) return null
  const row = cat(d.sid)
  clearDifferent(state, h.id)
  if (!row || isCust(row)) return null
  const sugg = suggestionsFrom(d.read, row).suggestions
  const g = sugg.find((s) => s.field === 'grade') || null
  /* the qualifier follows the grade — his ruling, the same day */
  const q = sugg.find((s) => s.field === 'qualifier') || null
  const y = yearOf('', row)
  switchToRow(h, row, { grade: g ? g.value : autoGradeFor(row), year: y ? String(y) : '', qualifier: !!q })
  clearPending(state, h.id, 'grade')
  clearPending(state, h.id, 'qualifier')
  if (g) setPending(state, h.id, 'grade', g)
  if (q) setPending(state, h.id, 'qualifier', q)
  /* The hero's ✨, "matching a first identification" — his ruling on the
   * renders. ⚠ `source: 'different'` is how the view leaves out "Not the
   * right note?": that button reverts to a BLANK CUSTOM note, which would
   * strip a catalogue note of its identity. */
  setPending(state, h.id, IDENTITY_FIELD, { value: row.sid, source: 'different', engine: d.read.engine, ms: d.read.ms })
  return row
}

/**
 * ══ HIS RULING 2 OF 2026-10-26 — THE YEAR FROM THE PHOTO ═══════════════
 * *"Fill the year from the photo's series when the field is empty, with ✨,
 * on every custom note."* The four-digit year in the reading's `series` —
 * either prompt returns one: *"1934A"*, *"1934-D"*, a colonial *"May 10,
 * 1775"* — goes into the holding's year when that is EMPTY (rule 2 of 4.3:
 * never over a typed value), marked on `YEAR_FILL_FIELD`. A custom note
 * only: a catalogue note's year is its row's. A failed reading fills
 * nothing. Returns true when it wrote; the caller saves.
 */
export function fillYearFromReading(state, h, read) {
  if (!read || read.ok !== true || !isCust(cat(h.catId))) return false
  if (String(h.year || '').trim()) return false
  const y = yearOfText(read.fields && read.fields.series)
  if (!y) return false
  h.year = String(y)
  setPending(state, h.id, YEAR_FILL_FIELD, { value: h.year, source: 'read', engine: read.engine, ms: read.ms })
  return true
}

/**
 * A landed upload verdict → values written and markers set.
 *
 * ⚠⚠⚠ RULE 2, HIS: **ONLY EMPTY FIELDS. It never overwrites anything a
 * collector has typed.** What "empty" means is per field and each one is
 * stated rather than assumed:
 *
 *   grade      "still the app's automatic starting grade" — his ruling,
 *              because a note NEVER has an empty grade.
 *   qualifier  THE TOGGLE IS OFF — read through `qualifierOn`, so a
 *              collector whose letters have not been carried over yet
 *              counts as ON and is left alone. ⚠⚠ Setting it is now a
 *              BOOLEAN on the holding (db/012, his ruling 2026-09-21);
 *              ~~adding the mark to the memo with `withMark`~~ was how
 *              this worked for the few hours between 4.3 and that
 *              ruling, and the memo is not touched here at all any more.
 *
 * ⚠⚠⚠ AND THE AI NEVER TURNS IT OFF — HIS RULING 1, 2026-09-21:
 * *"The AI turns it ON only when it reads EPQ or PPQ on the label;
 * otherwise it stays off. The AI never turns it off — only a collector
 * can."* **That is a property of the loop, not of a branch:** the only
 * write below is `= true`, and `qualifierProposal` refuses anything
 * that is not a mark, so `false` is unreachable from here. There is no
 * `else` to get it wrong in. `pending-check` §6d asserts it as
 * BEHAVIOUR over an ON holding and a no-qualifier reading, and
 * statically that no `qualifier = false` exists in this file.
 *
 * ⚠ IT IS WIRED TO `uploadSlot`'s `onChange`, which fires only when the
 * upload SUCCEEDED and the landing is the CURRENT generation — a stale
 * landing is a reading of a photograph already replaced. Re-asserted
 * here anyway, because a seam's meaning is a thing a later edit changes.
 */
export function applyReading(state, verdict) {
  if (!state || !verdict || verdict.ok !== true || verdict.stale === true) return null
  const read = verdict.read
  const h = (state.collection || []).find((x) => x.id === verdict.holdingId)
  if (!h) return null
  /* ══ HIS RULING OF 2026-10-22 — A REPLACED FRONT THE APP DID NOT CHECK ══
   * A new landing supersedes an earlier unchecked line on this note. Then:
   * the slot carried an `r2:` ref BEFORE this upload (`editor.js` captures
   * it ahead of `putPhoto`, which rewrites the ref to `idb:`; `uploadSlot`
   * carries it on the verdict as `previousRef`) — a REPLACEMENT — and the
   * reading neither confirmed nor contradicted the note: NO reading came
   * back at all (`read` null — his check, 2026-10-22: the early return
   * below used to swallow exactly this shape, and the 2026-10-19 case may
   * have been it), or it failed (`read.ok` false), or it is a raw reading
   * of the SAME denomination (`rawDifferent` says `same`), or this is a
   * custom note with a typed Fr (`identifyNote` says `typed-fr`) — and, his
   * rulings 2(a) and 2(b) of 2026-10-25, a raw reading whose denomination
   * cannot be compared, none read included (`incomparableDenominationRaw`),
   * or a label reading `matchFr` refuses (`inconclusiveLabel`) — and, his
   * answer 5 on Other, a label on an Other the collector kept (`identifyNote`
   * says `kept-other`), which converts nothing. One line,
   * memory-only (pending.js), nothing on the holding, no call. A first
   * photo (`idb:` or no ref before) draws nothing. */
  clearUnchecked(state, h.id)
  const replaced = /^r2:/.test(String(verdict.previousRef == null ? '' : verdict.previousRef))
  if (!read) {
    if (replaced) setUnchecked(state, h.id, 'no-reading')
    return null
  }
  /* ══ STEP 7.3(c) — IDENTITY IS DECIDED FIRST AND VALUES SECOND ═════
   * The order is not a style choice (finding 1 of the scope): the grade
   * fill's "is this still the automatic grade" test is asked against the
   * ROW, so the row has to be settled before it is asked. */
  const identity = identifyNote(state, h, read)
  if (replaced) {
    const why = read.ok !== true ? 'read-failed'
      : identity && identity.silent === 'typed-fr' ? 'typed-fr'
        : identity && identity.silent === 'kept-other' ? 'kept-other'
        : identity === null && sameDenominationRaw(h, read) ? 'same-denomination'
          : identity === null && incomparableDenominationRaw(h, read) ? 'incomparable-denomination'
            : identity === null && inconclusiveLabel(h, read) ? 'inconclusive-label' : null
    if (why) setUnchecked(state, h.id, why)
  }
  const note = cat(h.catId)
  const out = suggestionsFrom(read, note)
  out.identity = identity
  out.filled = []
  out.refused = []

  /* ⚠ 7.5: a photo of a DIFFERENT note fills nothing on THIS one. Its
   * grade is that note's, and the prompt's Update is where it goes. */
  if (identity && identity.different) {
    for (const s of out.suggestions) out.refused.push({ field: s.field, reason: 'different-note' })
    return out
  }
  /* ══ HIS RULING 2 OF 2026-10-26 — THE YEAR FROM THE PHOTO ══════════════
   * After the 7.5 guard, so a different-note photo fills no year; after
   * identity, so a note this landing converted is a catalogue row by now
   * and keeps the row's year (`convertToRow`). Every note still custom
   * here — a raw reading, a refused label, a typed Fr — fills. */
  const yearFilled = fillYearFromReading(state, h, read)
  if (yearFilled) out.filled.push('year')
  /* ⚠ STEP 6: a RAW reading carries no grade or qualifier to suggest; what
   * it filled (the denomination, his ruling 2) is already saved by
   * `fillDenomination`, and is reported here so the caller can say so. */
  if (identity && identity.raw) {
    for (const s of out.suggestions) out.refused.push({ field: s.field, reason: 'raw-reading' })
    out.filled.push(...identity.filled)
    /* the year was filled after `fillDenomination` saved, so it saves here */
    if (yearFilled) { save(); republish() }
    return out
  }

  let stamped = false
  for (const s of out.suggestions) {
    if (s.field === 'grade') {
      if (gradeIsCollectors(h, note)) { out.refused.push({ field: 'grade', reason: 'collector-picked' }); continue }
      h.grade = s.value
      setPending(state, h.id, 'grade', s)
      /* ⚠ OTHER'S STEP 5, HIS (b): the label's grade STAMPED on a custom entry
       * (`gradeRead`, private inside `custom_entry`), so entering Other keeps a
       * VF20 the label gave after the ✨ is gone (custom.js `enterOther`) */
      if (isCust(note)) { note.gradeRead = s.value; stamped = true }
      out.filled.push('grade')
      continue
    }
    if (s.field === 'qualifier') {
      if (qualifierOn(h)) { out.refused.push({ field: 'qualifier', reason: 'already-on' }); continue }
      /* ⚠ ON, and nothing else. The memo is the collector's and this
       * module no longer writes a byte of it. */
      h.qualifier = true
      setPending(state, h.id, 'qualifier', s)
      out.filled.push('qualifier')
      continue
    }
  }

  /* ══ ⚠⚠⚠ THE FILL IS SAVED, AND IT WAS NOT UNTIL HE ASKED ═══════════
   *
   * **HIS QUESTION, 2026-09-21: after `applyReading` fills the grade or
   * EPQ/PPQ, is the note actually saved?** It was NOT. Measured: zero
   * storage writes. ⚠⚠ Autosave fires on a collector's tap or keystroke
   * — `save()` is called BY the handlers in `main.js`, never by the
   * upload's landing — so an AI fill sat in memory and on screen and
   * **would have been lost on the next reload and never reached the
   * sync.** A value the collector can SEE and the app has not STORED is
   * the worst of both: it reads as saved and is not.
   *
   * ⚠ `republish()` goes with it because THE GRADE IS PUBLIC. Every
   * other site that changes a grade does `save · republish · render`
   * (`main.js`'s grade tap, the `#cgn` handler); a fill that saved but
   * did not republish would leave a stranger's profile showing the old
   * grade until something else happened to republish.
   *
   * ⚠⚠ ONLY WHEN SOMETHING WAS ACTUALLY FILLED. A landing that filled
   * nothing — every field already the collector's — must not write, or
   * the app would save on every photograph for no reason and a `save()`
   * is a full-replace sync. `republish` is itself a no-op unless the
   * profile is public (`community.js:25`). */
  if (stamped) saveCustom()
  if (out.filled.length) { save(); republish() }
  return out
}
